import React, { useEffect, useMemo, useState } from "react";
import { PhoneOutgoing, Upload, Users, RotateCcw, PhoneCall, Clock, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useContextStateManagement } from "../../context/ContextApi";
import { DEFAULT_PRACTICE_TIMEZONE } from "../../practiceTimezone";
import InfoTooltip from "../../components/common/InfoTooltip";
import "./InboundOverview.css";

// Past 2h / Past 6h / Today - same rework as InboundOverview.jsx's identical picker (see its own
// comment for the full rationale). "Today" has no fixed hour count - it always runs from the
// practice's own local midnight through now.
const TIME_RANGES = [
  { key: "2h", label: "Past 2h", hours: 2 },
  { key: "6h", label: "Past 6h", hours: 6 },
  { key: "today", label: "Today", hours: null, isToday: true },
];

// Bucket resolution for the rolling-window graph - see InboundOverview.jsx's identical constant
// for the full rationale (gridlines/labels are computed separately via buildHourTicks below).
const ROLLING_WINDOW_BUCKETS = 48;

// Best-effort timezone for the one path with no server data at all to read from (the API-failed
// fallback branch below) - see InboundOverview.jsx's identical constant for the full rationale.
const FALLBACK_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_PRACTICE_TIMEZONE;

const GRAPH_MIN = 0;
const GRAPH_TOP = 2;
const GRAPH_BOTTOM = 30;
const GRAPH_VIEWBOX_HEIGHT = 36;
const GRAPH_LEFT = 6;
const GRAPH_RIGHT = 94;

const safeDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));

  if (!totalSeconds) return "0m";

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const minutesRemainder = minutes % 60;
    return `${hours}h ${minutesRemainder}m`;
  }

  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }

  return `${minutes}m ${remainingSeconds}s`;
};

const mapGraphValueToY = (value, axisMax) => {
  const normalizedValue = Math.max(GRAPH_MIN, Math.min(axisMax, Number(value) || 0));
  const graphRange = axisMax - GRAPH_MIN;

  if (graphRange === 0) {
    return GRAPH_BOTTOM;
  }

  return (
    GRAPH_BOTTOM -
    ((normalizedValue - GRAPH_MIN) / graphRange) * (GRAPH_BOTTOM - GRAPH_TOP)
  );
};

// niceStep/buildAxisScale/buildAxisScaleMultipleOfFive: identical to InboundOverview.jsx's own
// (see there for the full rationale) - snaps the y-axis to round numbers instead of an arbitrary
// ceil-to-multiple-of-4 step.
const niceStep = (rawStep) => {
  if (rawStep <= 1) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceNormalized;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;
  return niceNormalized * magnitude;
};

const buildAxisScale = (values) => {
  const highestValue = Math.max(1, ...values.map((value) => Number(value) || 0));
  const axisStep = niceStep(highestValue / 4);
  const axisMax = axisStep * 4;
  const axisLabels = Array.from({ length: 5 }, (_, index) => axisMax - index * axisStep);
  return { axisMax, axisLabels };
};

const buildAxisScaleMultipleOfFive = (values) => {
  const highestValue = Math.max(1, ...values.map((value) => Number(value) || 0));
  const axisStep = Math.max(5, Math.ceil(highestValue / 4 / 5) * 5);
  const axisMax = axisStep * 4;
  const axisLabels = Array.from({ length: 5 }, (_, index) => axisMax - index * axisStep);
  return { axisMax, axisLabels };
};

const buildPointCoords = (values, axisMax) => {
  if (!values.length) return [];

  return values.map((value, index) => {
    const x =
      values.length === 1
        ? (GRAPH_LEFT + GRAPH_RIGHT) / 2
        : GRAPH_LEFT + (index / (values.length - 1)) * (GRAPH_RIGHT - GRAPH_LEFT);
    const y = mapGraphValueToY(value, axisMax);
    return [x, y];
  });
};

const buildLinePath = (points) => {
  if (!points.length) return "";

  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x} ${y} L ${x + 0.1} ${y}`;
  }

  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point[0]} ${point[1]}`;
    }

    const previous = points[index - 1];
    const midpointX = (previous[0] + point[0]) / 2;
    return `${path} C ${midpointX} ${previous[1]}, ${midpointX} ${point[1]}, ${point[0]} ${point[1]}`;
  }, "");
};

const buildAreaPath = (points) => {
  const linePath = buildLinePath(points);
  if (!linePath) return "";

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];

  return `${linePath} L ${lastPoint[0]} ${GRAPH_BOTTOM} L ${firstPoint[0]} ${GRAPH_BOTTOM} Z`;
};

// formatDateKeyLabel: "YYYY-MM-DD" -> "Jul 23", for the graph header - kept independent of
// xAxisLabels since those switch to time-of-day strings for a single-day range (below).
const formatDateKeyLabel = (dateKey) => {
  const date = safeDate(`${dateKey}T00:00:00`);
  if (!date) return dateKey;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
};

// findFirstHourBoundaryAtOrAfter/buildHourTicks/bucketTimeFormatter: identical to
// InboundOverview.jsx's own (see there for the full rationale) - real clock-hour tick marks
// instead of 5 arbitrarily-interpolated labels.
const findFirstHourBoundaryAtOrAfter = (epochMs, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(epochMs));
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);
  const second = Number(parts.find((part) => part.type === "second")?.value || 0);
  const msIntoHour = minute * 60000 + second * 1000;
  return msIntoHour === 0 ? epochMs : epochMs + (3600000 - msIntoHour);
};

const buildHourTicks = (rangeStart, rangeEnd, timeZone) => {
  const startMs = rangeStart.getTime();
  const endMs = rangeEnd.getTime();
  const totalMs = endMs - startMs;
  if (!(totalMs > 0)) return [];

  const labelFormatter = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" });
  const ticks = [];
  let cursor = findFirstHourBoundaryAtOrAfter(startMs, timeZone);
  let guard = 0;
  while (cursor <= endMs && guard < 200) {
    const left = ((cursor - startMs) / totalMs) * 100;
    ticks.push({
      label: labelFormatter.format(new Date(cursor)),
      left: `${left}%`,
      align: left < 10 ? "start" : left > 90 ? "end" : "center",
    });
    cursor += 3600000;
    guard += 1;
  }
  return ticks;
};

const bucketTimeFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" });

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
// Beyond this many days, group into weekly buckets instead of one bucket per day - see
// InboundOverview.jsx's identical constant for the full rationale.
const MAX_DAILY_BUCKETS = 31;

// getDayCount: inclusive count of calendar days spanned by two "YYYY-MM-DD" date keys.
const getDayCount = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / ONE_DAY_MS) + 1);
};

// computeRangeBucketing: identical to InboundOverview.jsx's own - see there for the full
// rationale.
const computeRangeBucketing = (startDate, endDate) => {
  if (!startDate || !endDate || startDate === endDate) {
    return { bucketCount: ROLLING_WINDOW_BUCKETS, isSingleDay: true };
  }
  const dayCount = getDayCount(startDate, endDate);
  return dayCount <= MAX_DAILY_BUCKETS
    ? { bucketCount: dayCount, isSingleDay: false, isDaily: true }
    : { bucketCount: Math.ceil(dayCount / 7), isSingleDay: false, isDaily: false };
};

// formatWeekRangeLabel: compact "Jul 5-11" / "Jul 28-Aug 3" label for a bucket spanning multiple
// days, mirroring Dashboard.jsx's identical helper.
const formatWeekRangeLabel = (bucketStart, bucketEnd) => {
  const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
  const dayFormatter = new Intl.DateTimeFormat("en-US", { day: "numeric" });
  const startMonth = monthFormatter.format(bucketStart);
  const endMonth = monthFormatter.format(bucketEnd);
  if (startMonth === endMonth) {
    return `${startMonth} ${dayFormatter.format(bucketStart)}-${dayFormatter.format(bucketEnd)}`;
  }
  return `${startMonth} ${dayFormatter.format(bucketStart)}-${endMonth} ${dayFormatter.format(bucketEnd)}`;
};

// buildMultiDayXAxisLabels: identical to InboundOverview.jsx's own - see there for the full
// rationale.
const buildMultiDayXAxisLabels = (startTime, endTime, bucketCount, isDaily) => {
  if (isDaily) {
    const dayStart = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
    return Array.from({ length: bucketCount }, (_, index) => {
      const bucketDate = new Date(dayStart.getTime() + index * ONE_DAY_MS);
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(bucketDate);
    });
  }

  const totalMs = endTime.getTime() - startTime.getTime();
  const bucketWidthMs = totalMs / bucketCount;

  return Array.from({ length: bucketCount }, (_, index) => {
    const bucketStart = new Date(startTime.getTime() + index * bucketWidthMs);
    const bucketEnd = new Date(bucketStart.getTime() + bucketWidthMs - 1);
    return formatWeekRangeLabel(bucketStart, bucketEnd);
  });
};

const getAttemptDate = (entry, row) =>
  safeDate(
    entry?.startedAt ||
      entry?.CreateDateTime ||
      entry?.createDateTime ||
      row?.CreateDateTime ||
      row?.createDateTime,
  );

const QUEUE_STATUS_POLL_MS = 15000;

// QueuePanel: the current default face - practice-wide outbound queue/campaign counters from
// /api/outboundQueueStatus. pending/called/completed mirror the exact SendForCall/DoNotCall
// bucketing the backend already computes (practice-wide totals, not just this page's loaded
// rows - pagination on the Outbound grid means a leads-prop-derived count would never be
// reliable here).
const QueuePanel = () => {
  const { PracticeId, token, authorizedGetRequest, hasPageAccess } =
    useContextStateManagement();
  const canUploadLeads = hasPageAccess("bulkUpload", true);
  const navigate = useNavigate();
  const [queueStatus, setQueueStatus] = useState(null);
  const [queueError, setQueueError] = useState("");

  useEffect(() => {
    if (!PracticeId || !token) return undefined;

    let cancelled = false;

    const loadQueueStatus = async () => {
      try {
        const data = await authorizedGetRequest(token, "/api/outboundQueueStatus", {
          PracticeId,
        });
        if (!cancelled) {
          setQueueStatus(data);
          setQueueError("");
        }
      } catch (error) {
        if (!cancelled) {
          setQueueError("Could not load queue status.");
        }
      }
    };

    loadQueueStatus();
    const interval = window.setInterval(loadQueueStatus, QUEUE_STATUS_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
    // authorizedGetRequest intentionally excluded - ContextApi.js doesn't memoize its functions,
    // so including it would tear down/rebuild the poll interval on every unrelated context
    // re-render instead of only when PracticeId/token actually change (same rationale as
    // ManageLeadsPage.js's loadSchemaAndProviders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token]);

  const goToUpload = () => navigate("/bulk-upload");

  const callingNowCount = Number(queueStatus?.callingNow || 0);
  const pendingCount = Number(queueStatus?.pending || 0);
  const calledCount = Number(queueStatus?.called || 0);
  const completedCount = Number(queueStatus?.completed || 0);
  // "Progress through this list" = how much of the practice's outbound list has been fully
  // resolved (DoNotCall = true, see /api/outboundQueueStatus) - callingNow is a live in-progress
  // indicator layered on top and isn't part of this total, since a lead being dialed right now
  // is still counted under pending/called until that call resolves.
  const totalLeads = pendingCount + calledCount + completedCount;
  const progressPct = totalLeads > 0 ? Math.round((completedCount / totalLeads) * 100) : 0;

  const summaryText = !queueStatus
    ? ""
    : callingNowCount > 0
      ? `${formatCount(callingNowCount)} ${callingNowCount === 1 ? "person is" : "people are"} being called right now${
          pendingCount > 0 ? `, ${formatCount(pendingCount)} still waiting.` : "."
        }`
      : pendingCount > 0
        ? `No one is being called right now — ${formatCount(pendingCount)} ${pendingCount === 1 ? "lead is" : "leads are"} waiting to be called.`
        : "Everyone on this list has been called.";

  return (
    <article className="inbound-overview__card outbound-control-panel__card">
      {canUploadLeads && (
        <>
          <div className="outbound-control-panel__section">
            <div className="outbound-control-panel__section-head">
              <Upload size={16} strokeWidth={2} />
              <h3>Upload Leads</h3>
            </div>
            <p className="outbound-control-panel__hint">
              Review rows, columns, and validation before adding leads to this practice.
            </p>
            <button type="button" className="outbound-control-panel__action" onClick={goToUpload}>
              Preview &amp; Upload
            </button>
          </div>

          <div className="outbound-control-panel__divider" />
        </>
      )}

      <div className="outbound-control-panel__section">
        <div className="outbound-control-panel__section-head">
          <Users size={16} strokeWidth={2} />
          <h3>Queue</h3>
          <InfoTooltip text="Current status of the outbound calling list." />
        </div>
        {summaryText ? (
          <p className="outbound-control-panel__summary">{summaryText}</p>
        ) : null}
        {/* Campaign Status and Queue used to be two separate sections, but "Pending" and
            "Waiting" were the same queueStatus.pending value, and both sections repeated the
            same "Completed" value - one merged row of the 4 actually-distinct numbers instead. */}
        <div className="outbound-control-panel__stats outbound-control-panel__stats--four">
          <div className="outbound-control-panel__stat">
            <span
              className={`outbound-control-panel__stat-value${
                callingNowCount > 0 ? " outbound-control-panel__stat-value--active" : ""
              }`}
            >
              {formatCount(callingNowCount)}
            </span>
            <span className="outbound-control-panel__stat-label">
              <PhoneCall size={12} strokeWidth={2.4} />
              Calling Now
              <InfoTooltip text="Contacts currently being called." />
            </span>
          </div>
          <div className="outbound-control-panel__stat">
            <span className="outbound-control-panel__stat-value">
              {formatCount(pendingCount)}
            </span>
            <span className="outbound-control-panel__stat-label">
              <Clock size={12} strokeWidth={2.4} />
              Not Yet Called
              <InfoTooltip text="Contacts not yet called." />
            </span>
          </div>
          <div className="outbound-control-panel__stat">
            <span className="outbound-control-panel__stat-value">
              {formatCount(calledCount)}
            </span>
            <span className="outbound-control-panel__stat-label">
              <PhoneOutgoing size={12} strokeWidth={2.4} />
              Attempted
              <InfoTooltip text="Contacts called at least once." />
            </span>
          </div>
          <div className="outbound-control-panel__stat">
            <span className="outbound-control-panel__stat-value outbound-control-panel__stat-value--done">
              {formatCount(completedCount)}
            </span>
            <span className="outbound-control-panel__stat-label">
              <CheckCircle2 size={12} strokeWidth={2.4} />
              Completed
              <InfoTooltip text="Contacts marked complete." />
            </span>
          </div>
        </div>
        <div className="outbound-control-panel__progress">
          <div className="outbound-control-panel__progress-head">
            <span>Progress through this list</span>
            <span>{progressPct}%</span>
          </div>
          <div className="outbound-control-panel__progress-track">
            <div
              className="outbound-control-panel__progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
        {queueError ? <p className="outbound-control-panel__error">{queueError}</p> : null}
      </div>
    </article>
  );
};

// GraphPanel: the "Calls by Time" agent hero card + line graph - the flip side of QueuePanel
// above. Brought up to the same design InboundOverview.jsx's identical graph already has (real
// hour ticks, nice-round-number y-axis, no growing-block-size bug, Past 2h/6h/Today picker)
// instead of the older 2h/6h/12h/24h version this used to be stuck on.
const GraphPanel = ({
  practiceContactNumber,
  outboundContact,
  leads = [],
  startDate,
  endDate,
}) => {
  const { PracticeId, token, authorizedGetRequest, timezonePreferenceVersion } = useContextStateManagement();
  const [timeRangeKey, setTimeRangeKey] = useState("today");
  const [activePointIndex, setActivePointIndex] = useState(null);
  // Once a real date range is selected elsewhere on the page, the graph switches from a rolling
  // "last N hours from right now" window to that range - mirrors InboundOverview.jsx's identical
  // hasDateRange switch.
  const hasDateRange = Boolean(startDate) && Boolean(endDate);

  // Now covers BOTH a real calendar date range AND the rolling Past 2h/6h/Today window (via the
  // `hours`/`today` params) - see InboundOverview.jsx's identical effect for the full rationale.
  const [apiVolume, setApiVolume] = useState(null);
  const selectedRollingRange =
    TIME_RANGES.find((range) => range.key === timeRangeKey) || TIME_RANGES[TIME_RANGES.length - 1];

  useEffect(() => {
    if (!PracticeId || !token) {
      setApiVolume(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const params = hasDateRange
          ? {
              PracticeId,
              startDate,
              endDate,
              buckets: computeRangeBucketing(startDate, endDate).bucketCount,
            }
          : selectedRollingRange.isToday
            ? { PracticeId, today: true, buckets: ROLLING_WINDOW_BUCKETS }
            : { PracticeId, hours: selectedRollingRange.hours, buckets: ROLLING_WINDOW_BUCKETS };
        const data = await authorizedGetRequest(token, "/api/getOutboundCallVolume", params);
        if (!cancelled) {
          setApiVolume(data);
        }
      } catch (error) {
        if (!cancelled) {
          setApiVolume(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // authorizedGetRequest intentionally excluded - ContextApi.js doesn't memoize its functions,
    // so including it would re-fetch on every unrelated context re-render instead of only when
    // the date range/rolling window actually changes (same rationale as QueuePanel's poll effect
    // above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDateRange, PracticeId, token, startDate, endDate, selectedRollingRange.hours, selectedRollingRange.isToday, timezonePreferenceVersion]);

  // The hero card's KPI numbers (Total/Answered/Missed/Avg Duration) scope to the same calendar
  // date range as the "Calls by Time" graph below it, when one is selected - matching
  // InboundOverview.jsx's identical apiKpiTotals. With no range picked, they show true all-time
  // totals, same as before. Deliberately NOT scoped to the graph's own rolling 2h/6h/Today
  // picker (selectedRollingRange) - that's a graph-only zoom control, not a page-level filter.
  const [apiKpiTotals, setApiKpiTotals] = useState(null);

  useEffect(() => {
    if (!PracticeId || !token) {
      setApiKpiTotals(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const params = hasDateRange ? { PracticeId, startDate, endDate } : { PracticeId };
        const data = await authorizedGetRequest(token, "/api/getOutboundCallVolume", params);
        if (!cancelled) {
          setApiKpiTotals(data);
        }
      } catch (error) {
        if (!cancelled) {
          setApiKpiTotals(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token, hasDateRange, startDate, endDate, timezonePreferenceVersion]);

  const kpiStats = useMemo(
    () => ({
      totalCalls: apiKpiTotals?.totalCalls || 0,
      answeredCalls: apiKpiTotals?.answeredCalls || 0,
      missedCalls: apiKpiTotals?.missedCalls || 0,
      averageDuration: formatDuration(apiKpiTotals?.averageDurationSec || 0),
    }),
    [apiKpiTotals],
  );

  const overview = useMemo(() => {
    // No demo-looking fallback number - if the practice's own outbound number genuinely isn't
    // configured, say so plainly instead of silently showing a fake-looking one.
    const agentNumber = outboundContact || practiceContactNumber || "Not available";

    if (apiVolume) {
      const { bucketCount, isSingleDay, isDaily } = hasDateRange
        ? computeRangeBucketing(startDate, endDate)
        : { bucketCount: ROLLING_WINDOW_BUCKETS, isSingleDay: true, isDaily: false };
      const bucketCounts =
        Array.isArray(apiVolume.bucketCounts) && apiVolume.bucketCounts.length
          ? apiVolume.bucketCounts
          : new Array(bucketCount).fill(0);
      const graphValues = bucketCounts;

      // Multi-day y-axis is always in steps of 5 (per explicit request); the intraday/rolling
      // view keeps the general "nice round number" rule instead.
      const { axisMax, axisLabels: yAxisLabels } = isSingleDay
        ? buildAxisScale(graphValues)
        : buildAxisScaleMultipleOfFive(graphValues);
      const pointCoords = buildPointCoords(graphValues, axisMax);

      let axisTicks;
      let xAxisLabels;

      if (isSingleDay) {
        // The exact instants the backend actually bucketed by - see InboundOverview.jsx's
        // identical branch for the full rationale.
        const rangeStartMs = apiVolume.rangeStart ? new Date(apiVolume.rangeStart).getTime() : null;
        const rangeEndMs = apiVolume.rangeEnd ? new Date(apiVolume.rangeEnd).getTime() : null;
        const timeZone = apiVolume.practiceTimezone || DEFAULT_PRACTICE_TIMEZONE;

        if (rangeStartMs !== null && rangeEndMs !== null && rangeEndMs > rangeStartMs) {
          const bucketWidthMs = (rangeEndMs - rangeStartMs) / bucketCounts.length;
          const formatter = bucketTimeFormatter(timeZone);
          xAxisLabels = bucketCounts.map((_, index) =>
            formatter.format(new Date(rangeStartMs + index * bucketWidthMs)),
          );

          if (!hasDateRange && selectedRollingRange.isToday) {
            // "Today" can span nearly 24 hours depending on the time of day - one combined
            // label instead of one per hour, same fix as InboundOverview.jsx's identical branch.
            const hourOnlyFormatter = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" });
            axisTicks = [
              {
                label: `Data from ${hourOnlyFormatter.format(new Date(rangeStartMs))} to ${hourOnlyFormatter.format(new Date(rangeEndMs))}`,
                left: "50%",
                align: "center",
              },
            ];
          } else {
            axisTicks = buildHourTicks(new Date(rangeStartMs), new Date(rangeEndMs), timeZone);
          }
        } else {
          axisTicks = [];
          xAxisLabels = bucketCounts.map(() => "");
        }
      } else {
        // Multi-day: whole calendar dates, one combined "Data for Aug 1-15" mark instead of one
        // label per point - see InboundOverview.jsx's identical branch for the full rationale.
        const startTime = new Date(`${startDate}T00:00:00`);
        const endTime = new Date(`${endDate}T23:59:59.999`);
        const multiDayLabels = buildMultiDayXAxisLabels(startTime, endTime, bucketCount, isDaily);
        xAxisLabels = multiDayLabels;
        axisTicks = [
          { label: `Data for ${formatWeekRangeLabel(startTime, endTime)}`, left: "50%", align: "center" },
        ];
      }

      return {
        agentNumber,
        pointCoords,
        linePath: buildLinePath(pointCoords),
        areaPath: buildAreaPath(pointCoords),
        xAxisLabels,
        axisTicks,
        yAxisLabels,
        axisMax,
        graphValues,
      };
    }

    // Fallback for when the API call itself failed - counts from whatever `leads` the page
    // already has loaded (only leads already matched by phone number, so this undercounts vs
    // the API path above, same caveat as before this rework). No server data is available here
    // at all, so there's no PreferredTimezone to read - this is the one spot that genuinely has
    // nothing better than the browser's own clock to fall back on.
    const timeZone = FALLBACK_TIME_ZONE;
    const now = new Date();
    const startTime = selectedRollingRange.isToday
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
      : new Date(now.getTime() - selectedRollingRange.hours * 60 * 60 * 1000);

    const rows = (Array.isArray(leads) ? leads : [])
      .flatMap((row) => {
        const calls = Array.isArray(row?.Calls) ? row.Calls : [];
        if (calls.length === 0) {
          return [{ row, date: getAttemptDate(null, row) }];
        }
        return calls.map((entry) => ({ row: entry, date: getAttemptDate(entry, row) }));
      })
      .filter((item) => item.date && item.date >= startTime && item.date <= now)
      .sort((left, right) => left.date - right.date);

    const bucketWidthMs = (now.getTime() - startTime.getTime()) / ROLLING_WINDOW_BUCKETS;
    const bucketCounts = Array.from({ length: ROLLING_WINDOW_BUCKETS }, (_, index) => {
      const bucketStart = new Date(startTime.getTime() + index * bucketWidthMs);
      const bucketEnd = new Date(bucketStart.getTime() + bucketWidthMs);
      return rows.filter((item) => item.date >= bucketStart && item.date < bucketEnd).length;
    });
    const graphValues = bucketCounts;

    const { axisMax, axisLabels: yAxisLabels } = buildAxisScale(graphValues);
    const pointCoords = buildPointCoords(graphValues, axisMax);
    const formatter = bucketTimeFormatter(timeZone);
    const xAxisLabels = bucketCounts.map((_, index) =>
      formatter.format(new Date(startTime.getTime() + index * bucketWidthMs)),
    );
    const axisTicks = selectedRollingRange.isToday
      ? [
          {
            label: `Data from ${new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" }).format(startTime)} to ${new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric" }).format(now)}`,
            left: "50%",
            align: "center",
          },
        ]
      : buildHourTicks(startTime, now, timeZone);

    return {
      agentNumber,
      pointCoords,
      linePath: buildLinePath(pointCoords),
      areaPath: buildAreaPath(pointCoords),
      xAxisLabels,
      axisTicks,
      yAxisLabels,
      axisMax,
      graphValues,
    };
  }, [
    leads,
    outboundContact,
    practiceContactNumber,
    hasDateRange,
    startDate,
    endDate,
    apiVolume,
    selectedRollingRange.hours,
    selectedRollingRange.isToday,
  ]);

  const bottomMetrics = [
    {
      label: "Answered",
      value: kpiStats.answeredCalls,
      tone: "answered",
      info: "Outbound calls that were answered.",
    },
    {
      label: "Missed",
      value: kpiStats.missedCalls,
      tone: "missed",
      info: "Outbound calls that went unanswered.",
    },
    {
      label: "Avg Duration",
      value: kpiStats.averageDuration,
      tone: "duration",
      info: "Average length of answered outbound calls.",
    },
  ];

  const activePoint = overview.pointCoords[activePointIndex] || null;
  const activePointLabel = overview.xAxisLabels[activePointIndex] || "";
  const activePointValue = overview.graphValues[activePointIndex] ?? 0;
  const activePointLeft = activePoint ? `${activePoint[0]}%` : "0%";
  const activePointTop = activePoint
    ? `${(activePoint[1] / GRAPH_VIEWBOX_HEIGHT) * 100}%`
    : "0%";
  const tooltipSide =
    activePointIndex !== null && activePointIndex >= overview.pointCoords.length - 2
      ? "right"
      : "left";
  const gridLineYs = overview.yAxisLabels.map((label) => mapGraphValueToY(label, overview.axisMax));
  const yAxisTicks = overview.yAxisLabels.map((label) => ({
    label,
    top: `${(mapGraphValueToY(label, overview.axisMax) / GRAPH_VIEWBOX_HEIGHT) * 100}%`,
  }));
  // Plain hour marks (1 PM, 2 PM...), positioned by real elapsed time - decoupled from how many
  // data buckets exist (there are far more of those than would be readable as labels).
  const xAxisTicks = overview.axisTicks;

  return (
    <>
      <article className="inbound-overview__card inbound-overview__card--agent">
        <div className="inbound-overview__agent-top">
          <div className="inbound-overview__agent-head">
            <div className="inbound-overview__agent-icon">
              <PhoneOutgoing size={18} strokeWidth={2.2} />
            </div>
            <div className="inbound-overview__agent-copy">
              <div className="inbound-overview__agent-name">Outbound Agent</div>
              <div className="inbound-overview__agent-number">{overview.agentNumber}</div>
            </div>
          </div>
          <div className="inbound-overview__agent-total">
            <div className="inbound-overview__agent-total-label">
              Total Calls
              <InfoTooltip text="All outbound calls made." />
            </div>
            <div className="inbound-overview__agent-total-value">
              {formatCount(kpiStats.totalCalls)}
            </div>
          </div>
        </div>

        <div className="inbound-overview__agent-divider" />

        <div className="inbound-overview__metrics-row inbound-overview__metrics-row--agent">
          {bottomMetrics.map((metric) => (
            <article className="inbound-metric" key={metric.label}>
              <div className="inbound-metric__label">
                {metric.label}
                <InfoTooltip text={metric.info} />
              </div>
              <div className={`inbound-metric__value inbound-metric__value--${metric.tone}`}>
                {typeof metric.value === "number" ? formatCount(metric.value) : metric.value}
              </div>
            </article>
          ))}
        </div>
      </article>

      <article className="inbound-overview__card inbound-overview__card--graph">
        <div className="inbound-overview__graph-head">
          <div className="inbound-overview__graph-topline">
            <div className="inbound-overview__graph-title-group">
              <h3>
                Calls by Time
                <InfoTooltip text="Outbound call volume over the selected time range." />
              </h3>
            </div>
            <div className="inbound-overview__graph-controls">
              {hasDateRange ? (
                <span className="inbound-overview__filters-static">
                  {startDate === endDate
                    ? formatDateKeyLabel(startDate)
                    : `${formatDateKeyLabel(startDate)} – ${formatDateKeyLabel(endDate)}`}
                </span>
              ) : (
                <div className="inbound-overview__filters" role="tablist" aria-label="Outbound time filters">
                  {TIME_RANGES.map((range) => (
                    <button
                      key={range.key}
                      type="button"
                      className={`inbound-overview__filter${timeRangeKey === range.key ? " active" : ""}`}
                      onClick={() => setTimeRangeKey(range.key)}
                      aria-label={`Show ${range.isToday ? "today's" : `the past ${range.hours} hour`} range`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="inbound-overview__graph-shell">
          <div className="inbound-overview__y-axis">
            <div className="inbound-overview__y-axis-track">
              {yAxisTicks.map((tick, index) => (
                <span
                  key={`${tick.label}-${index}`}
                  className="inbound-overview__y-axis-tick"
                  style={{ top: tick.top }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          </div>

          <div className="inbound-overview__graph-content">
            <div
              className="inbound-overview__graph-scroll"
              style={{
                // Always exactly the space this card is given, never wider - see
                // InboundOverview.jsx's identical fix for the full rationale (this used to grow
                // one dedicated slot per label, which is the "block size increasing" bug).
                width: "100%",
              }}
            >
              <div className="inbound-overview__graph-stage" onMouseLeave={() => setActivePointIndex(null)}>
                {activePoint ? (
                  <>
                    <div
                      className="inbound-overview__guide-line"
                      style={{ left: activePointLeft }}
                    />
                    <div
                      className={`inbound-overview__tooltip inbound-overview__tooltip--${tooltipSide}`}
                      style={{ left: activePointLeft, top: activePointTop }}
                    >
                      <div className="inbound-overview__tooltip-time">{activePointLabel}</div>
                      <div className="inbound-overview__tooltip-value">
                        calls : {activePointValue}
                      </div>
                    </div>
                  </>
                ) : null}

                <div className="inbound-overview__graph">
                  <svg viewBox={`0 0 100 ${GRAPH_VIEWBOX_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
                    <defs>
                      <linearGradient id="outboundGraphFill" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#63C9DC" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#63C9DC" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {gridLineYs.map((y, index) => (
                      <path
                        key={`grid-${y}-${index}`}
                        className="inbound-overview__gridline"
                        d={`M 0 ${y} H 100`}
                      />
                    ))}
                    <path className="inbound-overview__area outbound-overview__area" d={overview.areaPath} />
                    <path className="inbound-overview__line" d={overview.linePath} />
                    {overview.pointCoords.map(([x, y], index) => (
                      <g
                        key={`${x}-${y}-${index}`}
                        className="inbound-overview__point-group"
                        onMouseEnter={() => setActivePointIndex(index)}
                      >
                        {index === activePointIndex ? (
                          <circle className="inbound-overview__point-glow" cx={x} cy={y} r="1.45" />
                        ) : null}
                        <circle
                          className={`inbound-overview__point${index === activePointIndex ? " inbound-overview__point--active" : ""}`}
                          cx={x}
                          cy={y}
                          r={index === activePointIndex ? "0.76" : "0.62"}
                        />
                      </g>
                    ))}
                  </svg>
                </div>
              </div>

              <div className="inbound-overview__x-axis">
                <div className="inbound-overview__x-axis-track">
                  {xAxisTicks.map((tick, index) => (
                    <span
                      key={`${tick.label}-${index}`}
                      className={`inbound-overview__x-axis-tick inbound-overview__x-axis-tick--${tick.align}`}
                      style={{ left: tick.left }}
                    >
                      {tick.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

// OutboundOverview: a flip card - front face is the queue/campaign-status panel (the current
// default view), back face is the "Calls by Time" graph, kept reachable via the flip button
// instead of deleted outright. Both faces occupy the exact same
// .inbound-workspace/.inbound-call-table-shell footprint (see Home.css) - the flip only changes
// which face is visible, never the outer box size.
const OutboundOverview = ({ practiceContactNumber, outboundContact, leads, startDate, endDate }) => {
  // Total Calls / Calls-by-Time (GraphPanel) is the default view - the queue counter
  // (QueuePanel) is reached via the flip button, not the other way around.
  const [isFlipped, setIsFlipped] = useState(true);

  return (
    <div className="outbound-flip-card">
      <button
        type="button"
        className="outbound-flip-card__toggle"
        onClick={() => setIsFlipped((current) => !current)}
        aria-label={isFlipped ? "Show queue status" : "Show call graphs"}
        title={isFlipped ? "Show queue status" : "Show call graphs"}
      >
        <RotateCcw size={14} strokeWidth={2.1} />
      </button>
      <div className={`outbound-flip-card__inner${isFlipped ? " is-flipped" : ""}`}>
        <div className="outbound-flip-card__face outbound-flip-card__face--front">
          <section className="inbound-overview outbound-control-panel">
            <QueuePanel />
          </section>
        </div>
        <div className="outbound-flip-card__face outbound-flip-card__face--back">
          <section className="inbound-overview">
            <GraphPanel
              practiceContactNumber={practiceContactNumber}
              outboundContact={outboundContact}
              leads={leads}
              startDate={startDate}
              endDate={endDate}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default OutboundOverview;
